import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiSun, FiMoon } = FiIcons;

const Header = ({ darkMode, setDarkMode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? darkMode 
            ? 'bg-luxury-darkCharcoal/95 backdrop-blur-lg shadow-lg border-b border-luxury-gold/20' 
            : 'bg-white/95 backdrop-blur-lg shadow-lg'
          : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          {/* Left Menu */}
          <motion.nav 
            className="hidden md:flex space-x-8"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {navItems.slice(0, 2).map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`text-sm font-medium transition-colors duration-300 hover:text-luxury-gold ${
                  location.pathname === item.path
                    ? 'text-luxury-gold'
                    : darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </motion.nav>

          {/* Logo */}
          <motion.div
            className="flex-shrink-0"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/">
              <img
                src="https://i.ibb.co/q3wdWB9T/Nashmi-barber-shop-Logo.jpg"
                alt="Nashmi Barber Shop"
                className="h-12 w-12 rounded-full border-2 border-luxury-gold hover:scale-110 transition-transform duration-300"
              />
            </Link>
          </motion.div>

          {/* Right Menu */}
          <motion.div 
            className="hidden md:flex items-center space-x-8"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <nav className="flex space-x-8">
              {navItems.slice(2).map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-sm font-medium transition-colors duration-300 hover:text-luxury-gold ${
                    location.pathname === item.path
                      ? 'text-luxury-gold'
                      : darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-full transition-colors duration-300 ${
                darkMode ? 'bg-luxury-gold text-black' : 'bg-gray-200 text-gray-900'
              }`}
            >
              <SafeIcon icon={darkMode ? FiSun : FiMoon} className="w-4 h-4" />
            </button>
          </motion.div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-full transition-colors duration-300 ${
                darkMode ? 'bg-luxury-gold text-black' : 'bg-gray-200 text-gray-900'
              }`}
            >
              <SafeIcon icon={darkMode ? FiSun : FiMoon} className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className={`md:hidden py-4 border-t ${
                darkMode ? 'border-luxury-gold/20' : 'border-gray-200'
              }`}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <nav className="flex flex-col space-y-4">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`text-sm font-medium transition-colors duration-300 hover:text-luxury-gold ${
                      location.pathname === item.path
                        ? 'text-luxury-gold'
                        : darkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;